import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Container,
  Flex,
  Heading,
  HStack,
  Input,
  Progress,
  SimpleGrid,
  Switch,
  Text,
  useColorMode,
  Wrap,
} from '@chakra-ui/react';
import { Chip, EmptyState, Pagination, Segmented, Skeleton, StatCard } from 'components/ds';
import { useState } from 'react';

const SEMANTIC_TOKENS = [
  'bg.canvas',
  'bg.surface',
  'bg.surfaceRaised',
  'bg.sidebar',
  'border.default',
  'border.strong',
  'text.default',
  'text.muted',
  'text.subtle',
  'primary.solid',
  'primary.hover',
  'primary.soft',
  'state.success',
  'state.warning',
  'state.error',
];

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box as='section' mb={10}>
    <Heading as='h2' mb={4} color='text.default' fontSize='lg'>
      {title}
    </Heading>
    {children}
  </Box>
);

/** DS living styleguide — preview tokens + kit (design-guidelines.md). Mounted at /styleguide. */
export const Styleguide = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const [seg, setSeg] = useState('all');
  const [page, setPage] = useState(3);

  return (
    <Box minH='100vh' color='text.default' fontFamily='body' bg='bg.canvas'>
      <Container maxW='960px' py={10}>
        <Flex align='center' justify='space-between' mb={8}>
          <Heading fontFamily='heading' fontSize='2xl'>
            Amatrium Design System
          </Heading>
          <Button onClick={toggleColorMode} size='ds-md' variant='ds-outline'>
            {colorMode === 'dark' ? '☀ Light' : '🌙 Dark'}
          </Button>
        </Flex>

        <Section title='Semantic colors'>
          <SimpleGrid columns={[2, 3, 5]} spacing={3}>
            {SEMANTIC_TOKENS.map((t) => (
              <Box
                key={t}
                overflow='hidden'
                borderWidth='1px'
                borderColor='border.default'
                borderRadius='md'
              >
                <Box h='48px' bg={t} />
                <Text p={2} color='text.muted' fontFamily='mono' fontSize='xs'>
                  {t}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Section>

        <Section title='Buttons'>
          <Wrap spacing={3}>
            <Button size='ds-md' variant='ds-primary'>
              Primary
            </Button>
            <Button size='ds-md' variant='ds-outline'>
              Outline
            </Button>
            <Button size='ds-md' variant='ds-ghost'>
              Ghost
            </Button>
            <Button size='ds-md' variant='ds-soft'>
              Soft
            </Button>
            <Button size='ds-md' variant='ds-danger'>
              Danger
            </Button>
            <Button size='ds-md' variant='ds-danger-soft'>
              Danger soft
            </Button>
            <Button size='ds-sm' variant='ds-primary'>
              Small
            </Button>
            <Button size='ds-lg' variant='ds-primary'>
              Large
            </Button>
            <Button isDisabled size='ds-md' variant='ds-primary'>
              Disabled
            </Button>
          </Wrap>
        </Section>

        <Section title='Badges'>
          <Wrap spacing={3}>
            <Badge variant='ds-neutral'>Neutral</Badge>
            <Badge variant='ds-primary'>Primary</Badge>
            <Badge variant='ds-success'>Success</Badge>
            <Badge variant='ds-warning'>Warning</Badge>
            <Badge variant='ds-error'>Error</Badge>
          </Wrap>
        </Section>

        <Section title='Chips & Segmented'>
          <Wrap mb={4} spacing={3}>
            <Chip active>Active</Chip>
            <Chip>Default</Chip>
            <Chip>Another</Chip>
          </Wrap>
          <Segmented
            items={[
              { value: 'all', label: 'All' },
              { value: 'docs', label: 'Documents' },
              { value: 'media', label: 'Media' },
            ]}
            value={seg}
            onChange={setSeg}
          />
        </Section>

        <Section title='Cards & Stats'>
          <SimpleGrid columns={[1, 2, 3]} spacing={4}>
            <StatCard
              label='Total queries'
              value='12,480'
              delta={{ value: '▲ 8.2%', direction: 'up' }}
            />
            <StatCard
              label='Active users'
              value='342'
              delta={{ value: '▼ 1.1%', direction: 'down' }}
            />
            <Card variant='ds-hover'>
              <CardBody p={6}>
                <Text fontWeight={600}>Hover card</Text>
                <Text color='text.muted' fontSize='sm'>
                  Shadow + border lift on hover.
                </Text>
              </CardBody>
            </Card>
          </SimpleGrid>
        </Section>

        <Section title='Inputs / Switch / Progress'>
          <Flex direction='column' gap={4} maxW='420px'>
            <Input bg='bg.surface' borderColor='border.strong' placeholder='Search…' />
            <HStack>
              <Switch defaultChecked variant='ds' />
              <Text color='text.muted' fontSize='sm'>
                Enable notifications
              </Text>
            </HStack>
            <Progress value={64} variant='ds' />
          </Flex>
        </Section>

        <Section title='Skeleton'>
          <Flex direction='column' gap={2} maxW='420px'>
            <Skeleton h='16px' w='60%' />
            <Skeleton h='16px' />
            <Skeleton h='16px' w='80%' />
          </Flex>
        </Section>

        <Section title='Empty state'>
          <Card>
            <CardBody>
              <EmptyState
                title='No documents yet'
                description='Upload files to start building your knowledge base.'
                action={
                  <Button size='ds-sm' variant='ds-primary'>
                    Upload
                  </Button>
                }
              />
            </CardBody>
          </Card>
        </Section>

        <Section title='Pagination'>
          <Card>
            <Pagination
              page={page}
              pageCount={12}
              onChange={setPage}
              label='Showing 21–40 of 240'
            />
          </Card>
        </Section>
      </Container>
    </Box>
  );
};

export default Styleguide;
